import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { adminFetch } from "@/lib/adminStepUp";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminSessions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmRevokeSelf, setConfirmRevokeSelf] = useState(null);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const r = await adminFetch("/api/admin/sessions/mine");
      if (!r.ok) {
        setError(`load_failed_${r.status}`);
        setItems([]);
        return;
      }
      const j = await r.json();
      setItems(Array.isArray(j.items) ? j.items : []);
    } catch (_e) {
      setError("load_failed_network");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const revoke = async (session_ref, is_current) => {
    if (is_current) {
      setConfirmRevokeSelf(session_ref);
      return;
    }
    const r = await adminFetch("/api/admin/sessions/mine/revoke", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_ref, confirm: false }),
    });
    if (r.ok) {
      toast.success("Session revoked.");
      load();
    } else {
      toast.error("Revoke failed.");
    }
  };

  const revokeAllOthers = async () => {
    const r = await adminFetch("/api/admin/sessions/mine/revoke_others", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (r.ok) {
      const j = await r.json();
      toast.success(`Revoked ${j.revoked_count} other sessions.`);
      load();
    } else {
      toast.error("Revoke-others failed.");
    }
  };

  const confirmSelfRevoke = async () => {
    if (!confirmRevokeSelf) return;
    const ref = confirmRevokeSelf;
    setConfirmRevokeSelf(null);
    const r = await adminFetch("/api/admin/sessions/mine/revoke", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_ref: ref, confirm: true }),
    });
    if (r.ok) {
      toast.success("Session revoked.");
      load();
    } else {
      toast.error("Revoke failed.");
    }
  };

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8" data-testid="admin-sessions-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Admin Sessions</h1>
          <p className="text-sm text-muted-foreground">
            Review and revoke your active admin elevations. Your normal user
            session is not affected.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={revokeAllOthers}
          data-testid="admin-sessions-revoke-others-btn"
        >
          Revoke all other sessions
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4" data-testid="admin-sessions-error">
          <AlertTitle>Failed to load</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Active sessions</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No active admin sessions.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((s) => (
                <li
                  key={s.session_ref}
                  className="flex flex-col gap-2 py-3 md:flex-row md:items-center md:justify-between"
                  data-testid={`admin-session-row-${s.session_ref}`}
                >
                  <div className="flex flex-col text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">
                        {s.session_ref}…
                      </span>
                      {s.is_current && (
                        <Badge data-testid="admin-session-current-badge">Current</Badge>
                      )}
                      <Badge variant="outline" className="capitalize">
                        {s.device_family || "other"}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Created {fmt(s.created_at)} · Last active {fmt(s.last_active_at)} · Expires {fmt(s.expires_at)}
                    </div>
                  </div>
                  <div>
                    <Button
                      variant={s.is_current ? "destructive" : "secondary"}
                      size="sm"
                      onClick={() => revoke(s.session_ref, s.is_current)}
                      data-testid={`admin-session-revoke-btn-${s.session_ref}`}
                    >
                      {s.is_current ? "Revoke current" : "Revoke"}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={!!confirmRevokeSelf}
        onOpenChange={(o) => { if (!o) setConfirmRevokeSelf(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke your current session?</AlertDialogTitle>
            <AlertDialogDescription>
              You will be immediately logged out of the admin console and
              must re-authenticate.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-session-self-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmSelfRevoke}
              data-testid="admin-session-self-confirm"
            >
              Revoke current session
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
